"use client";

import { c as _c } from "react/compiler-runtime";
import { Typography } from "@repo/ui/aops-components/typography";
import { useId, useState, type ReactNode } from "react";
import styles from "./CourseSection.module.css";
import { cn } from "@repo/ui/lib/utils";
import { CourseTag } from "@/app/components/CoursePage/CourseTag/CourseTag";
import { SelectableTile } from "@/app/components/CoursePage/SelectableTile";
export type CourseSectionProps = {
  title: string;
  subtitle: string;
  stepTag?: string;
  options: Array<{
    body: ReactNode;
    value: string;
    disabled?: boolean;
  }>;
  onSelect?: (value: string) => void;
  /** Initially selected option `value`, if present in `options` */
  defaultValue?: string | null;
  /**
   * Rendered after the content-width body, as a direct child of the section.
   */
  footer?: ReactNode;
  /**
   * Optional companion column beside the tile list + footer (e.g. empty
   * format column). Stretches to the combined height of both.
   */
  aside?: ReactNode;
  /** Stretch a single tile across its container instead of capping at 50%. */
  fillTileWidth?: boolean;
  /** Shared SelectableTile subgrid row count across columns */
  tileRows?: number;
  /** Optional fragment id for deep links (e.g. #formats, #sequence, #info) */
  id?: string;
};

/**
 *
 * @param title - The title of the section
 * @param subtitle - The subtitle of the section
 * @param options - The options to display
 * @param onSelect - The function to call when an option is selected
 * @param defaultValue - Optional initially selected option value
 * @param footer - Optional full-bleed content after the body
 * @param aside - Optional companion column beside tiles + footer
 * @param fillTileWidth - Stretch a single tile to the list container width
 * @param tileRows - Optional shared subgrid row count for the tile list
 * @returns
 */
export function CourseSection(t0) {
  const $ = _c(41);
  const {
    title,
    subtitle,
    stepTag,
    options,
    onSelect,
    defaultValue,
    footer,
    aside,
    fillTileWidth,
    tileRows,
    id
  } = t0;
  const titleId = useId();
  const bodyId = useId();
  let t1;
  if ($[0] !== defaultValue || $[1] !== options) {
    t1 = () => {
      if (defaultValue == null) {
        return null;
      }
      const index = options.findIndex(item => item.value === defaultValue);
      return index >= 0 ? index : null;
    };
    $[0] = defaultValue;
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [selectedIndex, setSelectedIndex] = useState(t1);
  const t2 = footer && styles.bodyWithFooter;
  let t3;
  if ($[3] !== t2) {
    t3 = cn(styles.body, t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onSelect || $[6] !== options || $[7] !== selectedIndex) {
    let t5;
    if ($[9] !== onSelect || $[10] !== selectedIndex) {
      t5 = (item_0, index_0) => {
        const isSelected = selectedIndex === index_0;
        const isCardSelectable = Boolean(onSelect) && !item_0.disabled;
        const pick = () => {
          if (!onSelect || item_0.disabled) {
            return;
          }
          setSelectedIndex(index_0);
          onSelect(item_0.value);
        };
        return <div key={item_0.value} role={isCardSelectable ? "button" : undefined} className={styles.optionCard} aria-pressed={isCardSelectable ? isSelected : undefined} aria-disabled={item_0.disabled} data-disabled={item_0.disabled || undefined} data-selectable={isCardSelectable || undefined} onClick={isCardSelectable ? pick : undefined}><div className={styles.optionCardDescription}>{item_0.body}</div></div>;
      };
      $[9] = onSelect;
      $[10] = selectedIndex;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = options.map(t5);
    $[5] = onSelect;
    $[6] = options;
    $[7] = selectedIndex;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[12] !== fillTileWidth || $[13] !== t4 || $[14] !== tileRows) {
    t5 = <SelectableTile.List rows={tileRows} fillWidth={fillTileWidth}>{t4}</SelectableTile.List>;
    $[12] = fillTileWidth;
    $[13] = t4;
    $[14] = tileRows;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== bodyId || $[17] !== t3 || $[18] !== t5) {
    t6 = <div id={bodyId} className={t3}>{t5}</div>;
    $[16] = bodyId;
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const tileList = t6;
  let t7;
  if ($[20] !== stepTag) {
    t7 = stepTag && <CourseTag>{stepTag}</CourseTag>;
    $[20] = stepTag;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== title || $[23] !== titleId) {
    t8 = <Typography id={titleId} as="h2" variant="h4">{title}</Typography>;
    $[22] = title;
    $[23] = titleId;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== subtitle) {
    t9 = <Typography variant="body">{subtitle}</Typography>;
    $[25] = subtitle;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <div className={styles.header}>{t7}{t8}{t9}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== aside || $[32] !== footer || $[33] !== tileList) {
    t11 = aside ? <div className={styles.splitBody}><div className={styles.splitMain}>{tileList}{footer}</div><div className={styles.splitAside}>{aside}</div></div> : <>{tileList}{footer}</>;
    $[31] = aside;
    $[32] = footer;
    $[33] = tileList;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== id || $[36] !== t10 || $[37] !== t11 || $[38] !== title || $[39] !== titleId) {
    t12 = <section id={id} className={styles.section} aria-label={title} aria-labelledby={titleId}>{t10}{t11}</section>;
    $[35] = id;
    $[36] = t10;
    $[37] = t11;
    $[38] = title;
    $[39] = titleId;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
}